import { useState, useCallback, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { api } from '../../utils/api';
import Button from '../common/Button';
import { TextInput } from '../common/TextInput';
import { FaMapMarkedAlt } from 'react-icons/fa';

interface AliexpressAddress {
  id: number;
  name: string;
  type: string;
  countryCode: string;
  score: number;
  parent?: AliexpressAddress | null;
}

interface AliexpressAddressMatchingModalProps {
  onClose: () => void;
  onSelect: (address: AliexpressAddress) => void;
  currentAddress: {
    customerName: string;
    customerAddressPhoneCountryCode: string;
    customerAddressPhone: string;
    customerAddressStreetNumber: string;
    customerAddressLine1: string;
    customerAddressLine2: string;
    customerAddressCity: string;
    customerAddressState: string;
    customerAddressPostalCode: string;
    customerAddressCountryCode: string;
  };
  countryCode: string;
}

export function AliexpressAddressMatchingModal({
  onClose,
  onSelect,
  currentAddress,
  countryCode,
}: AliexpressAddressMatchingModalProps) {
  const [searchQuery, setSearchQuery] = useState<string>(() => {
    const query = [
      currentAddress.customerAddressCity,
      currentAddress.customerAddressState,
    ].join(', ').trim();
    return query;
  });
  const [searchResults, setSearchResults] = useState<AliexpressAddress[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);


  const searchAddresses = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const queryParams = new URLSearchParams({
        query: query,
        type: 'city,province,town',
        countryCode: countryCode.toLowerCase(),
      });
      const response = await api.get(`/aliexpress/addresses/search?${queryParams.toString()}`);
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Error loading address options:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [countryCode]);

  // Trigger search when query changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchAddresses(searchQuery);
    }, 300); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchAddresses]);

  const handleAddressSelect = useCallback((address: AliexpressAddress) => {
    onSelect(address);
  }, []);

  return (
    <Modal
      onClose={onClose}
      width="medium"
    >
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-2">
            Match town, city or state against Aliexpress registered addresses
          </h2>
          <p className="text-lg">
            Current: {currentAddress.customerAddressCity}, {currentAddress.customerAddressState}
          </p>
        </div>

        {/* Search Input */}
        <div className="flex flex-row items-end gap-2">
          <TextInput
            label="Search for addresses"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Enter city, state..."
            containerClassName="flex-1"
          />
          <Button
            preset="white"
            onClick={() => {
              const addressQuery = encodeURIComponent(`${currentAddress.customerAddressStreetNumber}, ${currentAddress.customerAddressLine1}, ${currentAddress.customerAddressCity} ${currentAddress.customerAddressPostalCode}, ${currentAddress.customerAddressState}, ${currentAddress.customerAddressCountryCode}`);
              window.open(`https://maps.google.com/?q=${addressQuery}&z=3`, '_blank');
            }}
          >
            <FaMapMarkedAlt size={20} /> Open google maps
          </Button>
        </div>

        {/* Search Results */}
        <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="px-4 py-8 text-center text-gray-500">
              <div className="animate-spin inline-block w-6 h-6 border-2 border-current border-t-transparent rounded-full" />
              <div className="mt-2">Searching...</div>
            </div>
          ) : searchResults.length === 0 && searchQuery.trim() ? (
            <div className="px-4 py-8 text-center text-gray-500">
              No addresses found for "{searchQuery}"
            </div>
          ) : searchResults.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500">
              Enter a search query to find addresses
            </div>
          ) : (
            <div>
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-700">
                Found {searchResults.length} addresses
              </div>
              {searchResults.map((address) => (
                <AliexpressAddressRow
                  key={address.id}
                  address={address}
                  handleAddressSelect={handleAddressSelect}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}


function AliexpressAddressRow({
  address,
  handleAddressSelect,
}: {
  address: AliexpressAddress,
  handleAddressSelect: (address: AliexpressAddress) => void,
}) {
  return (
    <div
      key={address.id}
      onClick={() => handleAddressSelect(address)}
      className={`px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-200 last:border-b-0`}
    >
      <div className="flex flex-row items-stretch text-lg">
        <div className="flex flex-col flex-1">
          <span>
            {address.type}: {address.name}
          </span>
          {address.parent && (
            <span>{address.parent.type}: {address.parent.name}</span>
          )}
          {address.parent?.parent && (
            <span>{address.parent.parent.type}: {address.parent.parent.name}</span>
          )}
        </div>
        <span className="text-lg text-blue-600 font-medium ml-2 flex items-center flex-1">
          {(address.score * 100).toFixed(1)}% similarity
        </span>
      </div>
    </div>
  )
}